import { useCopy } from '../content'
import { BASE } from '../data/comuni'
import { SERVED_COMUNI, type ServedComune } from '../data/servedComuni'
import { CtaBand } from '../components/layout/CtaBand'
import { ZoneMap } from '../components/zone/ZoneMap'
import { Picture } from '../components/media/Picture'

/** The table's two halves (A–L, L–Z): side by side from lg, stacked below. */
const HALF = Math.ceil(SERVED_COMUNI.length / 2)
const HALVES: readonly (readonly ServedComune[])[] = [SERVED_COMUNI.slice(0, HALF), SERVED_COMUNI.slice(HALF)]

/**
 * Zone servite (spec §4.4): photo hero (the home hero's poster frame) with
 * the page's one `<h1>`, the territory map, the table of every comune served,
 * the line on the partner in the Valsesia, the paragraph on the neighbouring
 * cities outside the province, the CTA band.
 *
 * Hero contrast is held by the veil alone, whatever the photo: it stays at
 * ≥ black/60 down to 6rem above the bottom (white/85 text ≥ 4.6:1 even over a
 * pure-white pixel) and only the last 6rem, below every line of text
 * (`pb-36`), fades into the paper.
 *
 * The table (owner 2026-09-28: one table, all the comuni, compact, two
 * columns) lists the towns of data/servedComuni.ts alphabetically with one
 * checked fact each («Segni particolari», `copy.zone.notes`, like VV2). It is
 * one list split into two real `<table>`s with the same headers, side by side
 * from lg; each is labelled by the section heading. Two narrow columns fit a
 * phone, so no sideways scroll. Novara and Casale carry their province code;
 * Vercelli carries «la sede».
 */
export default function ZonePage() {
  const copy = useCopy()
  const z = copy.zone
  const th = 'pb-2 text-sm font-semibold text-vc2-accent'
  return (
    <main id="main">
      <section className="relative overflow-hidden text-white">
        <Picture role="hero" alt={copy.seo.zone.ogAlt} sizes="100vw" priority fill className="absolute inset-0" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/60 via-[calc(100%-6rem)] to-vc2-paper" />
        <div className="relative vc2-container pt-32 pb-36 md:pt-40 md:pb-44">
          <p className="vc2-kicker text-white/85">{z.eyebrow}</p>
          <h1
            tabIndex={-1}
            className="vc2-display mt-4 max-w-4xl text-[2.125rem] leading-[1.05] sm:text-5xl md:text-6xl"
          >
            {z.title}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/90">{z.intro}</p>
        </div>
      </section>

      <div className="vc2-container pt-8 pb-20 md:pb-28">
        <ZoneMap />

        <section className="mt-14" aria-labelledby="vc2-zone-all">
          <h2 id="vc2-zone-all" className="vc2-display text-2xl sm:text-3xl">
            {z.allComuniTitle}
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-x-12 lg:grid-cols-2">
            {HALVES.map((rows, i) => (
              <table key={i} aria-labelledby="vc2-zone-all" className="w-full text-left text-sm">
                <thead className={i > 0 ? 'max-lg:sr-only' : undefined}>
                  <tr className="border-b border-vc2-hairline">
                    <th scope="col" className={`${th} w-[40%] pr-4`}>
                      {z.headers.comune}
                    </th>
                    <th scope="col" className={th}>
                      {z.headers.note}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-vc2-hairline">
                  {rows.map((c) => (
                    <tr key={c.key}>
                      <th scope="row" lang="it" className="py-2 pr-4 align-top font-medium text-vc2-ink">
                        {c.name}
                        {c.province !== 'VC' && <>{' '}<span className="text-xs font-normal text-vc2-ink-soft">{c.province}</span></>}
                        {c.key === BASE.key && <>{' '}<span className="vc2-kicker font-normal text-vc2-accent">· {z.baseLabel}</span></>}
                      </th>
                      <td className="py-2 align-top text-vc2-ink-soft">{z.notes[c.key] ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
          </div>
        </section>

        {/* Full width, like the two table columns above it. The Valsesia is the partner's (owner 2026-09-28). */}
        <p className="mt-10 text-vc2-ink-soft">
          {z.partner.text}{' '}
          <a href="https://verdevalsesia.it" className="font-medium text-vc2-ink underline decoration-vc2-accent underline-offset-4 hover:decoration-current">
            {z.partner.link}
          </a>
          .
        </p>
        <p className="mt-4 text-vc2-ink-soft">{z.neighbours}</p>
      </div>

      <CtaBand title={copy.contact.title} text={copy.contact.intro} ctaLabel={copy.hero.ctaPrimary} />
    </main>
  )
}
